import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import type { CalendarProps } from "antd";
import dayLocaleData from "dayjs/plugin/localeData";
import { Calendar, notification, Spin } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import DataProvider from "../../apis";
import { useAppStore } from "../../store";
import LoadWrapper from "../../components/elements/load-wrapper";
import Colors from "../../constants/Colors";
import { NotificationType, schedulePassed } from "../../utils";
import { availableTimeSlots, toMinutes, findTimeFrame, type LibrarySlot } from "../../utils/date";

dayjs.extend(dayLocaleData);

type TimeFrame = {
  id?: number;
  startTime: string;
  endTime: string;
  booked?: boolean;
  disabled?: boolean;
  appointmentId?: null | number;
};

type Schedule = {
  date: string;
  timeFrames?: TimeFrame[];
};

type SlotState = "open" | "selected" | "booked" | "past" | "disabled";

// Morning/Afternoon/Evening used to be green/amber/orange — the amber and
// orange were both muted brownish tones that read as nearly identical at
// icon size. Afternoon is now a clear gold/yellow, distinct from Evening's
// orange at a glance.
const PERIOD_META: Record<number, { color: string; bg: string }> = {
  0: { color: "#16a34a", bg: "#f0fdf4" }, // Morning — green
  1: { color: "#ca8a04", bg: "#fefce8" }, // Afternoon — gold/yellow
  2: { color: "#ea580c", bg: "#fff7ed" }, // Evening — orange
};

// Precedence: disabled > past > booked > selected > open. Extends
// new-doctor-app's SchedulePeriodModal.resolveState with an actual `booked`
// branch — that app defines a 'booked' SlotState and a green style for it,
// but its resolveState never returns it (dead case), so a doctor can't
// currently tell a booked slot apart from a merely-selected one on either
// app today. Worth wiring up here regardless of mobile lagging: leaving a
// doctor unable to see that a slot is already booked means they could tap
// it thinking they're just freeing up an open slot, when a patient already
// has an appointment there.
function resolveState(
  slot: LibrarySlot,
  tf: TimeFrame | undefined,
  dateStr: string
): SlotState {
  if (tf?.disabled) return "disabled";
  if (schedulePassed(dateStr, slot.startTime)) return "past";
  if (tf?.booked) return "booked";
  if (tf) return "selected";
  return "open";
}

// Open vs. past/disabled used to be two shades of the same near-invisible
// gray with an identical border — genuinely hard to tell apart at a glance.
// Open is now a crisp white/bordered "tap me" look; past/disabled share a
// flatter, visibly-muted fill so both read as "not interactive" without
// needing to be distinguishable from each other.
const pillStyles: Record<SlotState, { bg: string; border: string; text: string }> = {
  open: { bg: "#ffffff", border: "#cbd5e1", text: "#334155" },
  selected: { bg: Colors.primary, border: Colors.primary, text: "#ffffff" },
  booked: { bg: "#f0fdf4", border: "#22c55e", text: "#15803d" },
  past: { bg: "#e2e8f0", border: "#cbd5e1", text: "#94a3b8" },
  disabled: { bg: "#e2e8f0", border: "#cbd5e1", text: "#94a3b8" },
};

export default function Availability() {
  const [api, contextHolder] = notification.useNotification();
  const { doctor } = useAppStore();
  const [timeframes, setTimeframes] = useState<TimeFrame[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState<Date>(new Date());
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  // Always-current snapshot for the mutation queue below — `timeframes` in
  // a closure captured before a queued mutation actually runs would be
  // stale if an earlier queued mutation already refetched.
  const timeframesRef = useRef<TimeFrame[]>([]);
  useEffect(() => {
    timeframesRef.current = timeframes;
  }, [timeframes]);

  // Serializes add/remove calls so two quick taps on different pills can't
  // race — new-doctor-app hits this same issue (SchedulePeriodModal.tsx)
  // with a semaphore: without it, a second tap can read the schedule cache
  // before the first tap's mutation has settled, then POST a timeframe list
  // that omits the slot the first tap just added, and the backend's
  // POST /schedule silently marks that omitted slot `disabled: true`.
  const mutationQueueRef = useRef<Promise<void>>(Promise.resolve());

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({ message: title, description: message });
  };

  const onPanelChange = (_value: Dayjs, _mode: CalendarProps<Dayjs>["mode"]) => {};
  const handleChange = (value: Dayjs) => setDate(new Date(value.toDate()));

  const fetchData = useCallback(
    async (forDate: Date) => {
      if (!doctor) return;
      setLoading(true);
      try {
        const formattedDate = moment(forDate).format("DD-MM-YYYY");
        const { data } = await DataProvider.getScheduleForDate(formattedDate);
        // /schedule/day/query has returned three different shapes in
        // production at different times — a bare Schedule[], a
        // { schedules: Schedule[] } wrapper, and a single Schedule object
        // for dates with only one row (new-doctor-app's scheduleApi.day
        // documents and normalises the same three cases).
        let schedules: Schedule[] = [];
        if (Array.isArray(data)) {
          schedules = data;
        } else if (data && Array.isArray((data as { schedules?: Schedule[] }).schedules)) {
          schedules = (data as { schedules: Schedule[] }).schedules;
        } else if (data && typeof data === "object" && "date" in data) {
          schedules = [data as Schedule];
        }
        const flt = schedules.find((item) => item.date === formattedDate);
        setTimeframes(flt?.timeFrames ?? []);
      } catch {
        setTimeframes([]);
      } finally {
        setLoading(false);
      }
    },
    [doctor]
  );

  useEffect(() => {
    fetchData(date);
  }, [fetchData, date]);

  const slotKey = (slot: LibrarySlot) => `${slot.startTime}-${slot.endTime}`;
  const markPending = (key: string, on: boolean) =>
    setPendingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  // Tapping a slot saves/removes it immediately — no separate "Save"
  // button. Matches new-doctor-app exactly: it POSTs/DELETEs per tap
  // (SchedulePeriodModal.tsx), not a batch-edit-then-save flow.
  function onSlotTap(slot: LibrarySlot, tf: TimeFrame | undefined, state: SlotState) {
    if (state === "past" || state === "disabled" || state === "booked") return;
    const key = slotKey(slot);
    if (pendingKeys.has(key)) return;
    markPending(key, true);
    mutationQueueRef.current = mutationQueueRef.current
      .then(() => (tf ? removeSlot(tf, key) : addSlot(slot, key)))
      .catch(() => {});
  }

  async function addSlot(slot: LibrarySlot, key: string) {
    try {
      // Resend the FULL current+desired set for the date, not just this one
      // slot — the backend's POST /schedule marks any existing timeframe
      // that ISN'T present in the incoming array as `disabled: true`,
      // permanently (nothing in that code path ever resets `disabled` back
      // to false). Deduped by minute-equivalence so a slot already stored
      // in a slightly different case/spacing isn't sent twice.
      const merged: { startTime: string; endTime: string }[] = [];
      const seen = new Set<string>();
      const pushUnique = (startTime: string, endTime: string) => {
        const k = `${toMinutes(startTime)}-${toMinutes(endTime)}`;
        if (seen.has(k)) return;
        seen.add(k);
        merged.push({ startTime, endTime });
      };
      for (const tf of timeframesRef.current) pushUnique(tf.startTime, tf.endTime);
      pushUnique(slot.startTime, slot.endTime);

      await DataProvider.saveSchedule({
        date: moment(date).format("DD-MM-YYYY"),
        timeFrames: merged,
      });
      await fetchData(date);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.message ?? "";
      // An "already exists" collision means the server already has this
      // slot stored in a slightly different format than the local library —
      // the tolerant `findTimeFrame` will pick it up once we refetch.
      if (/already exist/i.test(msg)) {
        await fetchData(date);
      } else {
        openNotificationWithIcon("warning", "Warning", msg || "Could not add this slot.");
      }
    } finally {
      markPending(key, false);
    }
  }

  async function removeSlot(tf: TimeFrame, key: string) {
    try {
      if (!tf.id) throw new Error("This slot has no id to remove.");
      await DataProvider.deleteTimeFrame(tf.id);
      await fetchData(date);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.message ?? "Could not remove this slot.";
      openNotificationWithIcon("warning", "Warning", msg);
    } finally {
      markPending(key, false);
    }
  }

  const dateStr = moment(date).format("DD-MM-YYYY");

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-medpharma-slate tracking-tight m-0">
          Availability
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Tap a slot to make yourself available — it saves immediately. Tap again to remove it.
        </p>
      </header>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card-hover overflow-hidden">
          <Calendar
            fullscreen={false}
            onChange={handleChange}
            onPanelChange={onPanelChange}
            className="appt-calendar-modern"
          />
        </div>

        {availableTimeSlots.map((period) => (
          <LoadWrapper loading={loading} key={period.id}>
            <PeriodCard
              name={period.name}
              color={PERIOD_META[period.id]?.color ?? Colors.primary}
              bg={PERIOD_META[period.id]?.bg ?? "#f8fafc"}
              slots={period.time}
              dateStr={dateStr}
              timeframes={timeframes}
              pendingKeys={pendingKeys}
              onToggle={onSlotTap}
            />
          </LoadWrapper>
        ))}
      </div>

      <SlotLegend />
      {contextHolder}
    </div>
  );
}

function SlotLegend() {
  const items: { label: string; state: SlotState }[] = [
    { label: "Open", state: "open" },
    { label: "Selected", state: "selected" },
    { label: "Booked", state: "booked" },
    { label: "Past / disabled", state: "past" },
  ];
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
      {items.map(({ label, state }) => (
        <div key={label} className="flex items-center gap-1.5">
          <span
            className="size-2.5 rounded-full border"
            style={{
              background: pillStyles[state].bg,
              borderColor: pillStyles[state].border,
            }}
          />
          <span className="text-xs text-slate-500">{label}</span>
        </div>
      ))}
    </div>
  );
}

function PeriodCard({
  name,
  color,
  bg,
  slots,
  dateStr,
  timeframes,
  pendingKeys,
  onToggle,
}: {
  name: string;
  color: string;
  bg: string;
  slots: LibrarySlot[];
  dateStr: string;
  timeframes: TimeFrame[];
  pendingKeys: Set<string>;
  onToggle: (slot: LibrarySlot, tf: TimeFrame | undefined, state: SlotState) => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={{ background: bg, color }}
        >
          <ClockCircleOutlined />
        </div>
        <h4 className="m-0 text-sm font-semibold text-slate-800 tracking-tight">{name}</h4>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {slots.map((slot) => {
          const tf = findTimeFrame(slot, timeframes);
          const state = resolveState(slot, tf, dateStr);
          const styles = pillStyles[state];
          const inactive = state === "past" || state === "disabled" || state === "booked";
          const key = `${slot.startTime}-${slot.endTime}`;
          const isPending = pendingKeys.has(key);
          return (
            <button
              key={key}
              type="button"
              disabled={inactive || isPending}
              onClick={() => onToggle(slot, tf, state)}
              className={`flex items-center justify-center rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                inactive ? "cursor-not-allowed" : "cursor-pointer hover:opacity-90"
              } ${state === "disabled" ? "line-through" : ""}`}
              style={{
                background: styles.bg,
                borderColor: styles.border,
                color: styles.text,
                opacity: isPending ? 0.6 : 1,
              }}
            >
              {isPending ? (
                <Spin size="small" />
              ) : (
                <>
                  {slot.startTime} - {slot.endTime}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
